import { useState, useEffect } from 'react';
import {
  fetchEventsContent,
  fetchPastEvents,
  fetchUpcomingEvents,
} from './eventsApi';
import { EventData, PastEventData, UpcomingEventData } from './types';

// Custom hook for fetching events content, past events (limited to 3), and upcoming events (limited to 3)
export const useGetEventsContent = () => {
  const [eventsData, setEventsData] = useState<EventData | null>(null);
  const [pastEvents, setPastEvents] = useState<PastEventData[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEventData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch events content, past events, and upcoming events in parallel
        const [eventsResponse, pastEventsResponse, upcomingEventsResponse] =
          await Promise.all([
            fetchEventsContent(),
            fetchPastEvents(),
            fetchUpcomingEvents(),
          ]);

        // Handle events content
        if (eventsResponse.status === 200 && eventsResponse.data.length > 0) {
          setEventsData(eventsResponse.data[0]);
        }

        // Handle past events - limit to 3 for display
        if (pastEventsResponse.succeeded && pastEventsResponse.data) {
          setPastEvents(pastEventsResponse.data.slice(0, 3));
        }

        // Handle upcoming events - limit to 3 for display
        if (upcomingEventsResponse.succeeded && upcomingEventsResponse.data) {
          setUpcomingEvents(upcomingEventsResponse.data.slice(0, 3));
        }

        if (
          !eventsResponse.data.length &&
          !pastEventsResponse.succeeded &&
          !upcomingEventsResponse.succeeded
        ) {
          setError('Failed to load events content');
        }
      } catch (err) {
        setError('Error loading events content');
        console.error('Events content loading error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, []);

  return { eventsData, pastEvents, upcomingEvents, isLoading, error };
};

// Custom hook for fetching ALL past events (for dedicated past events page)
export const useGetAllPastEvents = () => {
  const [pastEvents, setPastEvents] = useState<PastEventData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPastEvents = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetchPastEvents();

        if (response.succeeded && response.data) {
          // Get ALL past events (no limit)
          setPastEvents(response.data);
        } else {
          setError('Failed to load past events');
        }
      } catch (err) {
        setError('Error loading past events');
        console.error('Past events loading error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadPastEvents();
  }, []);

  return { pastEvents, isLoading, error };
};

// Custom hook for fetching ALL upcoming events (for dedicated upcoming events page)
export const useGetAllUpcomingEvents = () => {
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEventData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadUpcomingEvents = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetchUpcomingEvents();

        if (response.succeeded && response.data) {
          // Get ALL upcoming events (no limit)
          setUpcomingEvents(response.data);
        } else {
          setError('Failed to load upcoming events');
        }
      } catch (err) {
        setError('Error loading upcoming events');
        console.error('Upcoming events loading error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadUpcomingEvents();
  }, []);

  return { upcomingEvents, isLoading, error };
};
